import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Avatar,
  Paper,
} from '@mui/material';
import {
  Storage,
  Web,
  Api,
  Terminal,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';

const Skills = () => {
  const { darkMode } = useThemeMode();

  // Skills organized by categories with PNG icons
  const skillCategories = [
    {
      title: 'Frontend',
      icon: <Web />,
      color: '#61dafb',
      skills: [
        { name: 'React', icon: '/icons/React.png', color: '#61dafb' },
        { name: 'JavaScript', icon: '/icons/JavaScript.png', color: '#f7df1e' },
        { name: 'HTML5', icon: '/icons/HTML5.png', color: '#e34f26' },
        { name: 'CSS3', icon: '/icons/CSS3.png', color: '#1572b6' },
        { name: 'Material UI', icon: '/icons/Material UI.png', color: '#0081cb' },
        { name: 'Redux', icon: '/icons/Redux.png', color: '#764abc' },
      ],
    },
    {
      title: 'Backend',
      icon: <Api />,
      color: '#68a063',
      skills: [
        { name: 'Node.js', icon: '/icons/Node.js.png', color: '#68a063' },
        { name: 'Express', icon: '/icons/Express.png', color: '#000000' },
      ],
    },
    {
      title: 'Database',
      icon: <Storage />,
      color: '#4db33d',
      skills: [
        { name: 'MongoDB', icon: '/icons/MongoDB.png', color: '#4db33d' },
      ],
    },
    {
      title: 'Tools & Others',
      icon: <Terminal />,
      color: '#2496ed',
      skills: [
        { name: 'Docker', icon: '/icons/Docker.png', color: '#2496ed' },
        { name: 'GitHub', icon: '/icons/GitHub.png', color: '#181717' },
        { name: 'Postman', icon: '/icons/Postman.png', color: '#ff6c37' },
      ],
    },
  ];

  // Flatten all skills into a single array
  const allSkills = skillCategories.flatMap(category => category.skills);

  return (
    <Box id="skills" sx={{ 
      py: 2, 
      backgroundColor: darkMode 
        ? 'rgba(76, 175, 80, 0.02)' 
        : 'rgba(76, 175, 80, 0.01)' 
    }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
            <Terminal sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 800,
                background: 'linear-gradient(45deg, #1976d2, #dc004e)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Skills & Technologies
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
              fontSize: '1.1rem',
            }}
          >
            Technologies and tools I use to bring ideas to life
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ justifyContent: 'center' }}>
          {allSkills.map((skill, index) => (
            <Grid item key={index} xs={6} sm={4} md={3} lg={2}>
              <Paper
                elevation={0}
                sx={{
                  width: { xs: '100%', sm: 140 },
                  height: { xs: 120, sm: 140 },
                  minWidth: { xs: 120, sm: 140 },
                  background: darkMode
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                  border: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.12)'
                    : '1px solid rgba(0, 0, 0, 0.06)',
                  borderRadius: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: darkMode
                      ? `0 12px 24px rgba(0, 0, 0, 0.4), 0 0 20px ${skill.color}25`
                      : `0 12px 24px rgba(0, 0, 0, 0.15), 0 0 15px ${skill.color}20`,
                    border: `1px solid ${skill.color}40`,
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: `linear-gradient(90deg, ${skill.color}, ${skill.color}80)`,
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover::before': {
                    opacity: 1,
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 40, sm: 50 },
                    height: { xs: 40, sm: 50 },
                    borderRadius: '50%',
                    background: darkMode
                      ? `linear-gradient(135deg, ${skill.color}15, ${skill.color}08)`
                      : `linear-gradient(135deg, ${skill.color}12, ${skill.color}06)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px solid ${skill.color}20`,
                    boxShadow: `0 4px 12px ${skill.color}15`,
                  }}
                >
                  <Box
                    component="img"
                    src={skill.icon}
                    alt={skill.name}
                    sx={{
                      width: { xs: 24, sm: 28 },
                      height: { xs: 24, sm: 28 },
                      filter: darkMode ? 'brightness(1.1)' : 'none',
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    color: 'text.primary',
                    lineHeight: 1.2,
                    px: 1,
                    textAlign: 'center',
                  }}
                >
                  {skill.name}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Skills;